import { useCallback, useState } from 'react'
import {
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { PropTypes } from 'prop-types'
import { Box } from '@mui/system'
import { useController, useFormContext } from 'react-hook-form'

import { SELECT_VARIANTS, SELECT_POPOVER_PROPS } from './select.constants'
import { styles } from './select.styles'

const Select = ({
  variant,
  multiple,
  options,
  name,
  defaultValue,
  icon,
  placeholder,
  selectStyles,
  value: externalValue,
  onChange: externalOnChange,
  displayEmpty = false,
  ...props
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [isOpen, setIsOpen] = useState()

  // Check if we're inside a FormProvider context
  let formContext
  try {
    formContext = useFormContext()
  } catch (e) {
    formContext = null
  }

  // Use form context if available, otherwise use external props
  const isControlledByForm = formContext && name

  let value, onChange, ref
  if (isControlledByForm) {
    const { control, register } = formContext
    const controller = useController({
      register,
      control,
      defaultValue: defaultValue || (multiple ? [''] : ''),
      name,
    })
    value = controller.field.value
    onChange = controller.field.onChange
    ref = controller.field.ref
  } else {
    // Standalone mode - use external value/onChange
    value = externalValue !== undefined ? externalValue : defaultValue || (multiple ? [''] : '')
    onChange = externalOnChange || (() => {})
    ref = null
  }

  const isFiltered = multiple ? !value.includes('') : value !== ''
  const handleChange = useCallback(
    (event) => {
      const {
        target: { value: eventValue },
      } = event

      const isEmpty =
        (Array.isArray(eventValue) && eventValue.includes('') && isFiltered) ||
        eventValue.length === 0
      const filledValue =
        typeof eventValue === 'string'
          ? eventValue.split(',')
          : eventValue.filter((item) => item !== '')

      let onChangeValue
      if (isEmpty && multiple) {
        onChangeValue = ['']
      } else if (multiple) {
        onChangeValue = filledValue
      } else {
        onChangeValue = eventValue
      }

      onChange(onChangeValue)
    },
    [isFiltered, onChange, multiple]
  )
  const handleOpen = () => setIsOpen(!isOpen)

  const renderValue = () => {
    let optionsItems = placeholder

    if (isFiltered && multiple) {
      optionsItems = options
        .filter((option) => value.includes(option?.value))
        .map((itemsSelected) => itemsSelected?.label)
        .join(', ')
    } else if (isFiltered && !multiple) {
      optionsItems = options.find((option) => option?.value === value)?.label
    }

    return (
      <Box sx={styles.valueContainer}>
        {icon && <SvgIcon sx={styles.startAdornment} component={icon} />}
        <Typography sx={styles.value} component="span">
          {optionsItems}
        </Typography>
      </Box>
    )
  }

  return (
    <MuiSelect
      sx={[
        styles.select,
        styles[variant],
        isFiltered && styles[`${variant}Filtered`],
        isOpen && styles[`${variant}SelectOpened`],
        ...(Array.isArray(selectStyles) ? selectStyles : [selectStyles]),
      ]}
      inputRef={ref}
      multiple={!!multiple}
      value={value}
      displayEmpty={displayEmpty}
      onOpen={handleOpen}
      onClose={handleOpen}
      onChange={(event) => {
        const selectedValue = event.target.value
        const selectedOption = options.find((opt) => opt.value === selectedValue)

        // Block selecting disabled-like options
        if (selectedOption?.nonSelectable) return

        handleChange(event)
      }}
      input={<OutlinedInput />}
      renderValue={renderValue}
      autoWidth={!isMobile}
      fullWidth={isMobile}
      MenuProps={SELECT_POPOVER_PROPS.BOTTOM_RIGHT}
      {...props}
    >
      {placeholder && (
        <MenuItem sx={styles.menuItem} value="">
          {placeholder}
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem
          sx={styles.menuItem}
          key={option.value}
          value={option.value}
          // disabled={option?.isDisabled}
        >
          {option?.label}
        </MenuItem>
      ))}
    </MuiSelect>
  )
}

Select.propTypes = {
  variant: PropTypes.oneOf(Object.values(SELECT_VARIANTS)),
  multiple: PropTypes.bool,
  options: PropTypes.array,
  icon: PropTypes.func,
  name: PropTypes.string, // Optional - required only for form mode
  defaultValue: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  selectStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]), // For standalone mode
  onChange: PropTypes.func, // For standalone mode
}
Select.defaultProps = {
  variant: SELECT_VARIANTS.DEFAULT,
  multiple: false,
  options: [],
  icon: null,
  defaultValue: '',
  name: null,
  value: undefined,
  onChange: undefined,
}
export default Select
